import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional custom fallback renderer. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * App-wide error boundary. Without this, any runtime throw in a lazily-loaded
 * route (e.g. a `.map` on an unexpected API shape) white-screens the whole app.
 * Catches render errors, shows a recoverable fallback, and reports to the
 * console (and Sentry, if wired on window).
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
    const sentry = (window as unknown as { Sentry?: { captureException?: (e: unknown) => void } }).Sentry;
    sentry?.captureException?.(error);
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div
        role="alert"
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: '24px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 48, lineHeight: 1 }} aria-hidden="true">⚠️</div>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--heka-gray-800, #1f2937)', margin: 0 }}>
          Something went wrong
        </h1>
        <p style={{ fontSize: 14, color: 'var(--heka-gray-500, #6b7280)', maxWidth: 420, margin: 0 }}>
          An unexpected error occurred while loading this page. You can try again, or return to your dashboard.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={this.reset}
            style={{
              padding: '10px 20px',
              borderRadius: 'var(--radius-md, 8px)',
              border: 'none',
              background: 'var(--heka-primary-500, #0ea5e9)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => { window.location.href = '/'; }}
            style={{
              padding: '10px 20px',
              borderRadius: 'var(--radius-md, 8px)',
              border: '1px solid var(--heka-gray-300, #d1d5db)',
              background: '#fff',
              color: 'var(--heka-gray-700, #374151)',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Go home
          </button>
        </div>
      </div>
    );
  }
}
